// @flow

import moment from 'moment'
import { push, go, replace } from 'connected-react-router'
import type { APIError } from '../types'
import { getErrorAction } from '../utils/actions'
import type { Action } from '../types/actions/studentprofile'
import {
  getApiBaseUrlWithOrg,
  basicGetResponseOnly,
  checkHttpStatus,
  getApiHeaders
} from '../utils/api'
import { postToLoggingService } from './logging'
import { addActiveField } from '../utils/addActiveField'

export const GET_STUDENT_PROFILE_SUCCESS = 'GET_STUDENT_PROFILE_SUCCESS'
export const GET_STUDENT_PROFILE_FAILURE = 'GET_STUDENT_PROFILE_FAILURE'
export const GET_STUDENT_PROFILE_REQUEST = 'GET_STUDENT_PROFILE_REQUEST'
export const SET_STUDENT_PROFILE_ACTIVE_TAB = 'SET_STUDENT_PROFILE_ACTIVE_TAB'
export const SET_STUDENT_PROFILE_DATE_RANGE = 'SET_STUDENT_PROFILE_DATE_RANGE'
export const TOGGLE_ATTENDANCE_VIEW = 'TOGGLE_ATTENDANCE_VIEW'
export const WEEKLY_ENGAGEMENT_TOGGLE = 'WEEKLY_ENGAGEMENT_TOGGLE'

export function getStudentProfileSuccess(
  student: Object,
  courses: Array<Object>,
  reviews: Array<Object>,
  totalUserCoursesCount: number
): Action {
  return {
    type: GET_STUDENT_PROFILE_SUCCESS,
    student: student,
    courses: courses,
    reviews: reviews,
    totalUserCoursesCount: totalUserCoursesCount
  }
}

export function getStudentProfileFailure(error: APIError): Action {
  return getErrorAction(GET_STUDENT_PROFILE_FAILURE, error)
}

export function getStudentProfileRequest(): Action {
  return {
    type: GET_STUDENT_PROFILE_REQUEST
  }
}

export function setStudentProfileDateRange({
  startDate,
  endDate
}: Object): Action {
  return {
    type: SET_STUDENT_PROFILE_DATE_RANGE,
    payload: {
      startDate: moment(startDate).format('YYYY-MM-DD'),
      endDate: moment(endDate).format('YYYY-MM-DD')
    }
  }
}

export function toggleAttendanceView(isCourseViewToggled): Action {
  return {
    type: TOGGLE_ATTENDANCE_VIEW,
    isCourseViewToggled: isCourseViewToggled
  }
}

export function toggleEngagementView(isWeeklyEngagementToggle): Action {
  return {
    type: WEEKLY_ENGAGEMENT_TOGGLE,
    isWeeklyEngagementToggle: isWeeklyEngagementToggle
  }
}

export function fetchStudentProfile(studentId: number): Function {
  return async (dispatch, getState) => {
    try {
      const state = getState()

      dispatch(getStudentProfileRequest())

      // first, get user
      let url = `${getApiBaseUrlWithOrg(
        state.organization.orgId
      )}/users/${studentId}`
      const userResponse = await basicGetResponseOnly(state, dispatch, url)

      // don't let anyone view the student profile of a mentor
      if (!userResponse.user.roles.includes('qa_student')) {
        throw ({
          response: { status: 404, statusText: 'NOT FOUND' }
        })
      }

      url = `${getApiBaseUrlWithOrg(state.organization.orgId)}/users/${
        userResponse.user.id
      }/course_sections`
      const response = checkHttpStatus(
        await fetch(url, {
          method: 'GET',
          headers: getApiHeaders(state)
        })
      )
      const courses = await response.json()
      courses.forEach(course =>
        addActiveField(course, course.start_at, course.end_at)
      )

      dispatch(getStudentProfileSuccess(userResponse.user, courses))

      dispatch(
        postToLoggingService('student_page', {
          action: 'view',
          student: {
            id: userResponse.user.id,
            name: userResponse.user.name
          }
        })
      )
    } catch (error) {
      if(error?.response?.status) dispatch(getStudentProfileFailure(error))

      if (error?.response?.status === 404) {
        dispatch(push('/notfound'))
      }

      if (error?.response?.status === 500) {
        dispatch(push('/error'))
      }

      if(!error?.response?.status) {
        dispatch(replace('/notfound'))
        dispatch(go(0))
      }
    }
  }
}

export function postViewEffortAllToLogging({ student, masterCourse }: Object) {
  return async (dispatch: Function) => {
    dispatch(
      postToLoggingService('effort', {
        action: 'view_response_all',
        student,
        master_course: masterCourse
          ? {
              id: masterCourse.id,
              name: masterCourse.name
            }
          : null
      })
    )
  }
}

export function postViewEffortDetailToLogging({
  student,
  masterCourse,
  course,
  courseSection,
  hasEngagementNote,
  effortDate,
  mentor,
  effortValue
}: Object) {
  return async (dispatch: Function) => {
    dispatch(
      postToLoggingService('effort', {
        action: 'view_response',
        student,
        master_course: {
          id: masterCourse.id,
          name: masterCourse.name
        },
        course,
        course_section: courseSection,
        has_engagement_note: hasEngagementNote,
        effort_date: effortDate,
        effort_creator: {
          id: mentor.id,
          name: mentor.name
        },
        effort_value: effortValue
      })
    )
  }
}

export function postArtifactAssessmentViewAllToLogging({ student }: Object) {
  return async (dispatch: Function) => {
    dispatch(
      postToLoggingService('artifact_assessment', {
        action: 'view_all',
        student,
        is_student: false
      })
    )
  }
}
